/**
 * The month heading, turned into the way you reach a different month or year.
 *
 * The page head already had prev/next arrows, and they step one month. That is
 * the right control for "what is due next month" and the wrong one for anything
 * further: reaching next June from this one is eleven clicks, and reaching a
 * year back is thirteen with no indication that it is even possible. The heading
 * was the obvious place to put the answer, because it is the thing already
 * naming the month you are looking at.
 *
 * ## Why not two more buttons in the actions strip
 *
 * `.crm-head-act` is shrinkable so it can wrap, which means every control added
 * there is taken out of the TITLE's width first (see the note on the class
 * itself, and the head that had to be fixed once already for exactly this). The
 * strip is at four controls. A month and a year dropdown would be six, on the
 * one page whose title is a word plus a number and has nowhere to shrink to.
 *
 * ## Why a grid of months and not a `<select>`
 *
 * The picker inside the next-step dialog uses react-day-picker's own dropdown
 * caption, because there the calendar owns the caption and we style what it
 * renders. Here the markup is ours, and twelve months laid out three across is
 * one glance and one click, where a select is a click, a scroll and a click. The
 * year sits above them with its own pair of arrows, so a year is a step rather
 * than a list to hunt through - which also means there is no upper or lower
 * bound to invent.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { cn, Popover, PopoverContent, PopoverTrigger } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { monthNames } from "./lib";

export function MonthPicker({
  year,
  month,
  locale,
  label,
  onPick,
}: {
  year: number;
  /** 0-based, like `Date` and like the rest of this section. */
  month: number;
  locale: string;
  /** What the heading reads when closed, already formatted and capitalised. */
  label: string;
  onPick: (year: number, month: number) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  // The year being BROWSED, which is not the year selected until a month is
  // clicked. Stepping to 2027 and closing without choosing must leave the
  // calendar where it was, so this is local and resets on every open.
  const [browseYear, setBrowseYear] = useState(year);
  const months = useMemo(() => monthNames(locale), [locale]);

  // Reset on every open, so browsing to 2029 and closing without choosing does
  // not leave the next open sitting in 2029.
  useEffect(() => {
    if (open) setBrowseYear(year);
  }, [open, year]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("calendar.pickMonth")}
          title={t("calendar.pickMonth")}
          // Inherits the h1's face rather than restating it: this button lives
          // inside `.crm-h1`, and a font stack copied here would drift the first
          // time the heading is retuned.
          className={
            "-mx-2 inline-flex items-center gap-1.5 rounded-2xl px-2 text-left font-[inherit] " +
            "text-[length:inherit] leading-[inherit] tracking-[inherit] transition-colors " +
            "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 " +
            "focus-visible:ring-ring/40"
          }
        >
          {label}
          {/* The caret, and the whole reason it is here: a heading that opens a
              menu looks exactly like a heading that does not. The rest of the
              CRM already says "there is a list under this" with a chevron that
              turns over when it is open (the project switcher, the saved views,
              the filter fold), so this is that same sentence rather than a new
              one. Muted and a size below the text, so it reads as a marker on
              the title instead of as a second word in it. */}
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[268px] p-3" align="start">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setBrowseYear((y) => y - 1)}
            aria-label={t("calendar.prevYear")}
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-semibold tabular-nums">{browseYear}</span>
          <button
            type="button"
            onClick={() => setBrowseYear((y) => y + 1)}
            aria-label={t("calendar.nextYear")}
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1">
          {months.map((name, m) => {
            // Highlighted only when BOTH match. Stepping the year away from the
            // current one must clear the mark, or December 2027 would wear the
            // ring that says "this is the month you are looking at".
            const current = m === month && browseYear === year;
            return (
              <button
                key={name}
                type="button"
                aria-current={current || undefined}
                onClick={() => {
                  onPick(browseYear, m);
                  setOpen(false);
                }}
                className={cn(
                  "rounded-lg px-2 py-2 text-sm capitalize transition-colors",
                  current
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "hover:bg-muted",
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
